<template>
	<section class="invoice-details" aria-label="Invoice details">
		<table class="header-section" :class="{ 'is-disabled': disabled }" aria-label="Invoice details fields">
			<tbody>
				<tr>
					<th id="business-label"><label for="business-name">Business name</label></th>
					<td headers="business-label" class="readonly-cell">
						<input id="business-name" type="text" :value="businessInfo" disabled class="field-input disabled-input" />
					</td>
					<th id="invoice-number-label"><label for="invoice-number">Invoice #</label></th>
					<td headers="invoice-number-label">
						<input
							id="invoice-number"
							type="text"
							:value="invoiceNumber"
							:disabled="disabled"
							@input="$emit('update:invoiceNumber', $event.target.value)"
							class="field-input"
							placeholder="Auto or manual #"
						/>
					</td>
				</tr>
				<tr>
					<th id="invoice-client-label">Client</th>
					<td headers="invoice-client-label">
						<div class="catalog-field">
							<slot name="client" />
							<AppointmentIcon name="chevron-down" :size="14" class="catalog-chevron" />
						</div>
					</td>
					<th id="purchase-order-label"><label for="purchase-order">Purchase order</label></th>
					<td headers="purchase-order-label">
						<input
							id="purchase-order"
							type="text"
							:value="purchaseOrder"
							:disabled="disabled"
							@input="$emit('update:purchaseOrder', $event.target.value)"
							class="field-input"
						/>
					</td>
				</tr>
				<tr>
					<th id="invoice-date-label"><label for="invoice-date">Date</label></th>
					<td headers="invoice-date-label" class="select-cell" :colspan="showDueDate ? 1 : 3">
						<DateRangePicker
							id="invoice-date"
							mode="single"
							use-fixed
							:dateFrom="invoiceDate"
							:disabled="disabled"
							trigger-label="Invoice date"
							placeholder="Select date"
							@update="({ dateFrom }) => $emit('update:invoiceDate', dateFrom)"
						>
							<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
						</DateRangePicker>
					</td>
					<template v-if="showDueDate">
						<th id="invoice-due-date-label"><label for="invoice-due-date">Due date</label></th>
						<td headers="invoice-due-date-label" class="select-cell">
							<DateRangePicker
								id="invoice-due-date"
								mode="single"
								use-fixed
								:dateFrom="dueDate"
								:disabled="disabled"
								trigger-label="Due date"
								placeholder="Select date"
								@update="({ dateFrom }) => $emit('update:dueDate', dateFrom)"
							>
								<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
							</DateRangePicker>
						</td>
					</template>
				</tr>
				<tr v-if="$slots.building">
					<th id="invoice-building-label">Building</th>
					<td headers="invoice-building-label" class="select-cell" colspan="3">
						<div class="catalog-field">
							<slot name="building" />
							<AppointmentIcon name="chevron-down" :size="14" class="catalog-chevron" />
						</div>
					</td>
				</tr>
			</tbody>
		</table>
	</section>
</template>

<script setup>
import DateRangePicker from '@/components/DateRangePicker.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';

defineProps({
	businessInfo: {
		type: String,
		default: '',
	},
	invoiceNumber: {
		type: [String, Number],
		default: '',
	},
	purchaseOrder: {
		type: String,
		default: '',
	},
	invoiceDate: {
		type: String,
		default: '',
	},
	dueDate: {
		type: String,
		default: '',
	},
	showDueDate: {
		type: Boolean,
		default: true,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
});

defineEmits(['update:invoiceNumber', 'update:purchaseOrder', 'update:invoiceDate', 'update:dueDate']);
</script>

<style scoped>
.invoice-details {
	--invoice-header-control-height: 40px;
	width: 720px;
	max-width: 100%;
	min-width: 0;
	font-family: var(--font-family-base, sans-serif);
}

.header-section {
	display: block;
	width: 100%;
	box-sizing: border-box;
	border-spacing: 0;
	border-top: 1px solid var(--border-color);
	border-inline: 1px solid var(--border-color);
}

.header-section tbody {
	display: grid;
	grid-template-columns: 132px minmax(0, 1fr) 132px minmax(0, 1fr);
}

.header-section tr { display: contents; }
.header-section td[colspan='3'] { grid-column: span 3; }

.header-section th,
.header-section td {
	border-bottom: 1px solid var(--border-color);
	border-right: 1px solid var(--border-color);
	vertical-align: middle;
	text-align: left;
}

.header-section tr > :last-child { border-right: 0; }

.header-section th {
	display: flex;
	align-items: center;
	padding: 8px 12px;
	font-size: 13px;
	line-height: 18px;
	font-weight: 500;
	color: var(--text-color-secondary);
}

.header-section th label {
	display: block;
	margin: 0;
	font: inherit;
	color: inherit;
}

.header-section td {
	display: flex;
	align-items: stretch;
	min-width: 0;
	padding: 0;
	background: var(--noc-background, #fff);
	cursor: text;
}

.header-section td.select-cell { cursor: pointer; }
.header-section td.readonly-cell,
.header-section.is-disabled td { cursor: default; }

.catalog-field {
	display: flex;
	flex: 1;
	position: relative;
	min-width: 0;
}

.catalog-chevron {
	position: absolute;
	top: 50%;
	right: 12px;
	transform: translateY(-50%);
	color: var(--text-color-secondary);
	pointer-events: none;
}

.header-section :deep(.catalog-search-input),
.header-section :deep(.date-range-picker) {
	display: flex;
	align-items: stretch;
	width: 100%;
	min-width: 0;
}

.field-input,
.header-section :deep(.catalog-search-input .field-input),
.header-section :deep(.drp-trigger) {
	display: block;
	align-self: stretch;
	width: 100%;
	min-width: 0;
	height: auto;
	min-height: var(--invoice-header-control-height);
	padding: 10px 12px;
	border: 0;
	border-radius: 0;
	background: transparent;
	color: var(--text-color);
	font: 400 14px/20px var(--font-family-base, sans-serif);
	box-sizing: border-box;
	box-shadow: none;
	outline: none;
	cursor: inherit;
	transition: background-color 0.1s ease;
	touch-action: manipulation;
}

.header-section :deep(.catalog-search-input .field-input) { padding-right: 36px; }

.header-section :deep(.drp-trigger) {
	display: flex;
	flex-direction: row-reverse;
	justify-content: space-between;
	gap: 8px;
}

.header-section :deep(.drp-trigger-text) {
	color: inherit;
	font: inherit;
}

.field-input:hover:not(:disabled),
.header-section :deep(.catalog-search-input .field-input:hover:not(:disabled)),
.header-section :deep(.drp-trigger:hover:not(:disabled)),
.header-section :deep(.field-input[aria-expanded='true']),
.header-section :deep(.drp-trigger[aria-expanded='true']) {
	background: var(--noc-background-secondary, #fafafa);
}

.field-input:focus,
.header-section :deep(.catalog-search-input .field-input:focus),
.header-section :deep(.drp-trigger:focus-visible) {
	/* Override the global input focus border so table cells do not shift. */
	border: 0 !important;
	outline: none;
	background: var(--noc-background-secondary, #fafafa);
	box-shadow: none;
}

.field-input::placeholder,
.header-section :deep(.catalog-search-input .field-input::placeholder) {
	color: var(--text-color-secondary);
	opacity: 0.65;
}

.field-input.disabled-input {
	color: var(--text-color);
	-webkit-text-fill-color: var(--text-color);
	opacity: 1;
}

.header-section :deep(.drp-dropdown) {
	box-sizing: border-box;
	max-width: calc(100vw - 16px);
	overflow: auto;
	overscroll-behavior: contain;
}

@media (max-width: 900px) {
	.header-section tbody {
		grid-template-columns: 132px minmax(0, 1fr);
	}
	.header-section td { border-right: 0; }
	.header-section td[colspan='3'] { grid-column: auto; }
}

</style>
